import React, { useEffect, useState } from "react";
import AdminLayout from "./AdminLayout";
import Pagination, { StatusBadge, KvRow, ReasonPrompt } from "./shared";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { useNavigate } from "react-router-dom";
import api, { formatEUR, formatCrypto } from "../../lib/api";
import { toast } from "sonner";
import { Search, Snowflake, Sun, ShieldAlert, ChevronRight, User as UserIcon } from "lucide-react";

export default function AdminCustomers() {
  const [data, setData] = useState({ customers: [], total: 0 });
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [drawerId, setDrawerId] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: "25" });
      if (q) params.set("q", q);
      const r = await api.get(`/admin/customers?${params.toString()}`);
      setData(r.data);
    } catch (e) { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [page, q]);

  return (
    <AdminLayout title="Customers" subtitle="Every registered user, their KYC status, and lifetime activity">
      <div className="xch-card p-5 mb-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by email"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            className="rounded-xl pl-9"
            data-testid="customers-search-input"
          />
        </div>
      </div>

      <div className="xch-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="customers-table">
            <thead>
              <tr className="bg-muted/50 border-b border-border">
                <Th>Email</Th><Th>Country</Th><Th>KYC</Th><Th right>Orders</Th><Th right>Volume</Th><Th>Created</Th><Th>State</Th><Th />
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="text-center py-12 text-muted-foreground text-sm">Loading…</td></tr>}
              {!loading && data.customers.length === 0 && <tr><td colSpan={8} className="text-center py-12 text-muted-foreground text-sm">No customers.</td></tr>}
              {data.customers.map((u) => (
                <tr key={u.id} className="border-b border-border hover:bg-muted/30 cursor-pointer transition-colors" onClick={() => setDrawerId(u.id)} data-testid={`customer-row-${u.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-foreground">{u.email}</div>
                    <div className="text-xs text-muted-foreground">{u.name}</div>
                  </td>
                  <td className="px-4 py-3 text-foreground">{u.country}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={u.kyc_status} />
                    <div className="text-[10px] text-muted-foreground mt-1">Tier {u.kyc_tier}</div>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums font-mono text-foreground">{u.completed_orders}/{u.total_orders}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-mono text-foreground font-semibold">{formatEUR(u.total_volume_eur)}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground tabular-nums">
                    {new Date(u.created_at).toLocaleDateString("en-IE", { day: "2-digit", month: "short", year: "numeric" })}
                  </td>
                  <td className="px-4 py-3">
                    {u.is_frozen ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-destructive"><Snowflake className="w-3 h-3" /> Frozen</span>
                                  : u.is_staff ? <span className="text-xs font-semibold text-primary">Staff</span>
                                  : <span className="text-xs text-muted-foreground">Active</span>}
                    {u.flag_count > 0 && <span className="ml-2 text-[10px] font-semibold text-amber-700 bg-amber-100 rounded-full px-1.5 py-0.5">{u.flag_count} flag</span>}
                  </td>
                  <td className="px-2 py-3 text-muted-foreground"><ChevronRight className="w-4 h-4" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 pb-3">
          <Pagination page={page} pageSize={25} total={data.total} onPage={setPage} />
        </div>
      </div>

      <CustomerDrawer customerId={drawerId} onClose={() => setDrawerId(null)} onRefresh={fetchData} />
    </AdminLayout>
  );
}

const Th = ({ children, right }) => (
  <th className={`px-4 py-2.5 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground ${right ? "text-right" : "text-left"}`}>{children}</th>
);

function CustomerDrawer({ customerId, onClose, onRefresh }) {
  const [data, setData] = useState(null);
  const [prompt, setPrompt] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!customerId) return;
    api.get(`/admin/customers/${customerId}`).then((r) => setData(r.data));
  }, [customerId]);

  const refresh = () => customerId && api.get(`/admin/customers/${customerId}`).then((r) => setData(r.data));

  const runAction = async (reason) => {
    try {
      const ep = { freeze: "freeze", unfreeze: "unfreeze", escalate: "escalate" }[prompt.action];
      await api.post(`/admin/customers/${customerId}/${ep}`, { reason });
      toast.success(`Customer ${prompt.action}d`);
      refresh(); onRefresh?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Sheet open={!!customerId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-[560px] sm:max-w-none overflow-y-auto" data-testid="customer-drawer">
        <SheetHeader><SheetTitle className="font-display text-xl">Customer detail</SheetTitle></SheetHeader>
        {!data ? <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div> : (
          <div className="mt-4 space-y-5">
            <div className="bg-muted/40 rounded-2xl p-3">
              <div className="font-display font-bold text-foreground">{data.customer.name}</div>
              <div className="text-sm text-muted-foreground">{data.customer.email}</div>
              <div className="flex items-center gap-2 mt-2">
                <StatusBadge status={data.customer.kyc_status} />
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Tier {data.customer.kyc_tier}</span>
                {data.customer.is_frozen && <span className="text-[10px] font-semibold text-destructive uppercase tracking-wider">Frozen</span>}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <Stat label="Orders" value={data.orders.length} />
              <Stat label="Wallets" value={data.wallets.length} />
              <Stat label="Flags" value={data.flags.filter((f) => f.status === "pending").length} />
            </div>

            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Orders</div>
              <div className="bg-white border border-border rounded-2xl divide-y divide-slate-100 max-h-72 overflow-y-auto">
                {data.orders.length === 0 ? (
                  <div className="text-sm text-muted-foreground p-3 text-center">No orders yet.</div>
                ) : data.orders.map((o) => (
                  <div key={o.id} className="flex items-center justify-between gap-2 p-2.5" data-testid={`cust-order-${o.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-foreground">{formatCrypto(o.receive_amount, 4)} {o.asset} <span className="text-muted-foreground font-normal">for {formatEUR(o.fiat_amount)}</span></div>
                      <div className="text-[11px] text-muted-foreground tabular-nums">{new Date(o.created_at).toLocaleString("en-IE")}</div>
                    </div>
                    <StatusBadge status={o.status} />
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Wallets used</div>
              <div className="bg-white border border-border rounded-2xl divide-y divide-slate-100">
                {data.wallets.length === 0 ? (
                  <div className="text-sm text-muted-foreground p-3 text-center">No wallets.</div>
                ) : data.wallets.map((w) => (
                  <div key={w.id} className="p-2.5 text-xs">
                    <div className="flex justify-between"><span className="font-semibold text-foreground">{w.asset} · {w.network}</span><span className="text-muted-foreground">{w.label}</span></div>
                    <div className="font-mono text-[11px] text-muted-foreground truncate">{w.address}</div>
                  </div>
                ))}
              </div>
            </div>

            {data.flags.length > 0 && (
              <div className="space-y-1">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Compliance flags</div>
                <div className="bg-white border border-border rounded-2xl divide-y divide-slate-100">
                  {data.flags.map((f) => (
                    <div key={f.id} className="p-2.5 text-xs flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-foreground">{f.reason}</div>
                        <div className="text-muted-foreground">{new Date(f.created_at).toLocaleString("en-IE")} · {f.entity_type}</div>
                      </div>
                      <StatusBadge status={f.status} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2 sticky bottom-0 bg-white pt-3 pb-2 border-t border-border">
              <Button onClick={() => navigate(`/admin/customers/${customerId}`)} className="bg-foreground hover:bg-foreground/90 text-white rounded-xl gap-1" size="sm" data-testid="customer-view-360-btn">
                <UserIcon className="w-3.5 h-3.5" /> View 360
              </Button>
              {data.customer.is_frozen ? (
                <Button onClick={() => setPrompt({ action: "unfreeze", label: "Unfreeze account" })} className="bg-primary hover:bg-primary/90 text-white rounded-xl gap-1" size="sm" data-testid="customer-unfreeze-btn">
                  <Sun className="w-3.5 h-3.5" /> Unfreeze
                </Button>
              ) : (
                <Button onClick={() => setPrompt({ action: "freeze", label: "Freeze account", danger: true })} variant="outline" size="sm" className="rounded-xl text-destructive border-destructive/30 hover:bg-destructive/5 gap-1" data-testid="customer-freeze-btn">
                  <Snowflake className="w-3.5 h-3.5" /> Freeze
                </Button>
              )}
              <Button onClick={() => setPrompt({ action: "escalate", label: "Escalate to review" })} variant="outline" size="sm" className="rounded-xl gap-1" data-testid="customer-escalate-btn">
                <ShieldAlert className="w-3.5 h-3.5" /> Escalate
              </Button>
            </div>
          </div>
        )}
        <ReasonPrompt
          open={!!prompt}
          onOpenChange={() => setPrompt(null)}
          title={prompt?.label || ""}
          description="This action is logged to the audit trail."
          onConfirm={runAction}
          danger={prompt?.danger}
        />
      </SheetContent>
    </Sheet>
  );
}

const Stat = ({ label, value }) => (
  <div className="bg-white border border-border rounded-2xl p-2.5 text-center">
    <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</div>
    <div className="font-display text-2xl font-extrabold text-foreground tabular-nums">{value}</div>
  </div>
);
